import { useEffect, useId, useState } from "react";
import * as api from "../../../lib/api.ts";
import { mediaUrl, useMediaUrl } from "../../media/media-url.ts";
import { useAssetUrl } from "../../media/asset-url.ts";
import { KindIcon, PlayIcon } from "../../media/icons.tsx";
import { TrashIcon } from "../../shell/icons.tsx";
import { ImageViewerDoor } from "../../image-viewer/lazy.tsx";
import { startIndex, type ResolveImage, type ViewerRequest } from "../../image-viewer/viewer-model.ts";
import { openLabel, playLabel } from "../../image-viewer/viewer-text.ts";
import {
  durationText,
  heavyMediaSize,
  heavyMediaNotes,
  mediaKindWord,
  mediaShownName,
  type MaterialItem,
  type MaterialThumb,
  type ViewerSource,
  viewerImagesOf,
} from "./material.ts";
import type { MaterialState } from "./use-material.ts";

/*
  Ряд плиток блока «Материал» формы выдачи: то, что уйдёт ученику, и вход в
  слой просмотра. Вынесен из `MaterialBlock.tsx` 17.10.2026, когда плитки
  получили кадры видео и строки о весе (docs/homework-media-form-player-plan.md).
  Грузится статически вместе с формой, поэтому тяжёлого здесь ничего: слой
  просмотра только через дверь `ImageViewerDoor`.
*/

/** Что убирает крестик: подпись по виду, чтобы читалка не называла аудио картинкой. */
function dropLabel(thumb: MaterialThumb): string {
  if (thumb.kind !== "media") return "Убрать картинку";
  return thumb.fileKind === "video" ? "Убрать видео" : "Убрать аудио";
}

/**
 * Адреса кадров видео ряда, разрешённые ОДИН раз на ряд (Р4
 * `docs/homework-media-form-player-plan.md`): из них читают и плитка, и слой.
 * Ключ это ключ строки, адрес есть только у живого кадра.
 *
 * ⚠️ Путь подписан на минуты, а перевыпуска кадра по файлу нет: протухший кадр
 * плитка помечает битым (`broke`), и дальше плитка рисует значок, а слой
 * получает `poster: null`, то есть чёрную коробку вместо битой картинки.
 * Битым помечается путь, а не строка: новый путь той же строки снова живой.
 */
function usePosterUrls(items: readonly MaterialItem[]): {
  posters: ReadonlyMap<string, string>;
  broke: (path: string) => void;
} {
  const pathOf = (it: MaterialItem): string | null => (it.thumb.kind === "media" ? it.thumb.posterPath : null);
  const wanted = [...new Set(items.flatMap((it) => pathOf(it) ?? []))].join("\n");
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (wanted === "") return;
    let alive = true;
    const paths = wanted.split("\n");
    void Promise.all(paths.map(async (path) => [path, await api.mediaAssetUrl(path)] as const))
      .then((pairs) => {
        if (alive) setUrls(new Map(pairs));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [wanted]);
  const posters = new Map(
    items.flatMap((it) => {
      const path = pathOf(it);
      const url = path === null || broken.has(path) ? undefined : urls.get(path);
      return url === undefined ? [] : [[it.key, url] as const];
    }),
  );
  const broke = (path: string): void => setBroken((was) => (was.has(path) ? was : new Set(was).add(path)));
  return { posters, broke };
}

/**
 * Адрес картинки для слоя просмотра (Р3 `docs/homework-image-viewer-plan.md`):
 * `blob` как есть, файл через `mediaUrl`, тот же кэш и тот же адрес ПОЛНОГО
 * файла, что у миниатюры вида `file`. Отказ любого рода это `null`, то есть
 * «Картинка не загрузилась.» в слайде.
 */
function resolveOf(sources: ReadonlyMap<string, ViewerSource>): ResolveImage {
  return async (key, { force }) => {
    const source = sources.get(key);
    if (source === undefined) return null;
    if (source.kind === "blob") return source.url;
    return mediaUrl(source.fileId, { force }).catch(() => null);
  };
}

/**
 * Файлы, которые уйдут ученику, в порядке показа у него.
 *
 * Картинка открывается крупно слоем поверх страницы (Р1, Р3 и Р6 плана). Список
 * для слоя снимается в момент нажатия.
 *
 * ⚠️ Пока `request` не `null`, «убрать» и «Повторить» выключены: пока едет чанк
 * просмотрщика, фон ещё не `inert`, а убранная строка отзывает свой `blob`, и
 * слайд получил бы отказ. Одно условие покрывает и ожидание чанка, и открытый
 * слой.
 */
export function MaterialStrip(props: { material: MaterialState; busy: boolean }) {
  const { items } = props.material;
  const [request, setRequest] = useState<ViewerRequest | null>(null);
  const { posters, broke } = usePosterUrls(items);
  const noteId = useId();
  // Список опустел, пока ехал чанк (`restore` у «Продолжить» и «Задать ещё
  // раз»): снимок устарел, и без сброса слой открылся бы сам, когда материал
  // появится снова
  if (items.length === 0 && request !== null) setRequest(null);
  if (items.length === 0) return null;
  const viewer = viewerImagesOf(items, posters);
  const viewerByKey = new Map(viewer.map((entry) => [entry.image.key, entry.image]));
  const locked = props.busy || request !== null;
  const heavy = heavyMediaNotes(items);
  const notes = heavy.lines.map((line) => ({ ...line, id: `${noteId}-${line.key}` }));
  const whyId = `${noteId}-why`;

  const open = (key: string, opener: HTMLElement): void => {
    const images = viewer.map((entry) => entry.image);
    setRequest({
      images,
      start: startIndex(images, key),
      resolve: resolveOf(new Map(viewer.map((entry) => [entry.image.key, entry.source]))),
      opener,
      noun: "image",
    });
  };

  /*
    Ряд это сетка одинаковых квадратов (Р7 плана): «убрать» у всех в одном
    углу, и ни одна плитка не тянет соседей. Строки о весе живут списком под
    рядом, а не под плиткой: под плиткой они делали её выше соседних
  */
  return (
    <>
      <ul className="hwmat__list">
        {items.map((item) => {
          const image = viewerByKey.get(item.key);
          const poster = posters.get(item.key) ?? null;
          const { thumb: t } = item;
          const thumb = (
            <Thumb
              thumb={t}
              fileId={item.fileId}
              poster={poster}
              onPosterError={() => {
                if (t.kind === "media" && t.posterPath !== null) broke(t.posterPath);
              }}
            />
          );
          const label =
            image === undefined
              ? null
              : image.media === undefined
                ? openLabel(image.alt)
                : playLabel(image.media.kind, image.alt);
          const note = notes.find((n) => n.key === item.key);
          return (
            <li key={item.key} className="hwmat__item" data-status={item.status}>
              {label === null ? (
                thumb
              ) : (
                <button
                  type="button"
                  className={t.kind === "media" ? "hwmat__open hwmat__open--play" : "hwmat__open"}
                  title={label}
                  aria-label={label}
                  aria-describedby={
                    note === undefined ? undefined : heavy.why === null ? note.id : `${note.id} ${whyId}`
                  }
                  onClick={(e) => open(item.key, e.currentTarget)}
                >
                  {thumb}
                </button>
              )}
              {item.status === "uploading" && (
                <span
                  className="hwmat__progress"
                  role="progressbar"
                  aria-label="Загрузка картинки"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(item.progress * 100)}
                >
                  <i style={{ width: `${Math.round(item.progress * 100)}%` }} />
                </span>
              )}
              <button
                type="button"
                className="btn btn--ico btn--danger hwmat__drop"
                title={dropLabel(t)}
                aria-label={dropLabel(t)}
                disabled={locked}
                onClick={() => props.material.remove(item.key)}
              >
                <TrashIcon />
              </button>
              {item.status === "failed" && (
                <FailedNote item={item} material={props.material} busy={locked} />
              )}
            </li>
          );
        })}
      </ul>
      {notes.length > 0 && (
        <ul className="hwmat__notes">
          {notes.map((n) => (
            <li key={n.key} id={n.id}>
              {n.text}
            </li>
          ))}
        </ul>
      )}
      {heavy.why !== null && (
        <p className="hwmat__notes" id={whyId}>
          {heavy.why}
        </p>
      )}
      <ImageViewerDoor request={request} onClose={() => setRequest(null)} />
    </>
  );
}

function FailedNote(props: { item: MaterialItem; material: MaterialState; busy: boolean }) {
  return (
    <span className="hwmat__fail" role="status">
      <span className="err">{props.item.error}</span>
      <button
        type="button"
        className="btn btn--quiet btn--sm"
        disabled={props.busy}
        onClick={() => props.material.retry(props.item.key)}
      >
        Повторить
      </button>
    </span>
  );
}

/**
 * Миниатюра по любому из источников адреса. Хуки зовутся всегда, а не по
 * ветке: иначе смена вида миниатюры у той же строки нарушила бы порядок хуков.
 *
 * У аудио и видео картинки нет: плитка со значком или кадром, именем и
 * длительностью. ⚠️ Вид `file` при отказе `<img>` становится нейтральной
 * плиткой «Файл», а не битой картинкой: так приезжает аудио из плана раздачи,
 * сохранённого до 16.10.2026, где вида файла ещё не было (Р6 плана).
 */
export function Thumb(props: {
  thumb: MaterialThumb;
  fileId: string | null;
  /** Адрес кадра видео, если он жив (`usePosterUrls`). */
  poster?: string | null;
  onPosterError?: () => void;
}) {
  const assetPath = props.thumb.kind === "path" ? props.thumb.path : null;
  const asset = useAssetUrl(assetPath);
  const file = useMediaUrl(props.thumb.kind === "file" ? props.fileId : null);
  const [broken, setBroken] = useState<string | null>(null);
  if (props.thumb.kind === "media") {
    return <MediaTile thumb={props.thumb} poster={props.poster ?? null} onPosterError={props.onPosterError} />;
  }
  const src =
    props.thumb.kind === "blob" ? props.thumb.url : props.thumb.kind === "path" ? asset : file.url;
  if (src === null) return <span className="hwmat__thumb hwmat__thumb--empty" />;
  if (props.thumb.kind === "file" && broken === src) {
    return <span className="hwmat__thumb hwmat__tile">Файл</span>;
  }
  return (
    <img
      className="hwmat__thumb"
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBroken(src)}
    />
  );
}

/**
 * Плитка аудио и видео (Р7 плана): кадр или значок по центру, «играть» поверх
 * кадра, длительность и вес плашками в левом верхнем углу (в правом корзина),
 * имя полосой внизу в одну строку. Полное имя в подписи кнопки.
 */
function MediaTile(props: {
  thumb: Extract<MaterialThumb, { kind: "media" }>;
  poster: string | null;
  onPosterError: (() => void) | undefined;
}) {
  const { thumb, poster } = props;
  const size = heavyMediaSize(thumb);
  return (
    <span className="hwmat__thumb hwmat__media">
      {poster === null ? (
        <KindIcon kind={thumb.fileKind} />
      ) : (
        <>
          <img className="hwmat__poster" src={poster} alt="" loading="lazy" onError={props.onPosterError} />
          <span className="hwmat__play">
            <PlayIcon />
          </span>
        </>
      )}
      {(thumb.durationMs !== null || size !== null) && (
        <span className="hwmat__badges">
          {thumb.durationMs !== null && <span className="hwmat__badge">{durationText(thumb.durationMs)}</span>}
          {size !== null && <span className="hwmat__badge">{size}</span>}
        </span>
      )}
      <span className="hwmat__name">{mediaShownName(thumb.name) ?? mediaKindWord(thumb.fileKind)}</span>
    </span>
  );
}
